import api from "./api";
import {
  User,
  UserCreate,
  UserUpdate,
  UserStats,
  AuthResponse,
  MobileAuthResponse,
  LoginCredentials,
  AuthenticationSession,
} from "../types";
import {
  clearMobileRefreshCredential,
  getMobileRefreshCredential,
  isNativeApp,
  setMobileRefreshCredential,
} from "./mobileAuth";

const USERS_ENDPOINT = "/users";

export const userService = {
  // Register a new user
  async register(userData: UserCreate): Promise<User> {
    const response = await api.post<User>(`${USERS_ENDPOINT}/register`, userData);
    return response.data;
  },

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    if (isNativeApp()) {
      const response = await api.post<MobileAuthResponse>(
        "/auth/mobile/login",
        credentials
      );
      try {
        await setMobileRefreshCredential(response.data.refresh_token);
      } catch (error: unknown) {
        await api.post<void>("/auth/mobile/logout", {
          refresh_token: response.data.refresh_token,
        });
        throw error;
      }
      return response.data;
    }
    const response = await api.post<AuthResponse>("/auth/login", credentials);
    return response.data;
  },

  async logout(): Promise<void> {
    if (isNativeApp()) {
      try {
        const refreshCredential = await getMobileRefreshCredential();
        if (refreshCredential) {
          await api.post<void>("/auth/mobile/logout", {
            refresh_token: refreshCredential,
          });
        }
      } finally {
        await clearMobileRefreshCredential();
      }
      return;
    }
    await api.post<void>("/auth/logout");
  },

  async listSessions(): Promise<AuthenticationSession[]> {
    return (await api.get<AuthenticationSession[]>("/auth/sessions")).data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await api.delete(`/auth/sessions/${encodeURIComponent(sessionId)}`);
  },

  async logoutAll(): Promise<void> {
    try {
      await api.post("/auth/logout-all");
    } finally {
      if (isNativeApp()) await clearMobileRefreshCredential();
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post("/auth/password/change", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  async requestRecovery(email: string): Promise<void> {
    await api.post("/auth/recovery/request", { email });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post("/auth/recovery/reset", { token, password });
  },

  async resendVerification(email: string): Promise<void> {
    await api.post("/auth/verification/resend", { email });
  },

  async confirmVerification(token: string): Promise<void> {
    await api.post("/auth/verification/confirm", { token });
  },

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>(`${USERS_ENDPOINT}/me`);
    return response.data;
  },

  // Update current user profile
  async updateCurrentUser(userData: UserUpdate): Promise<User> {
    const response = await api.put<User>(`${USERS_ENDPOINT}/me`, userData);
    return response.data;
  },

  // Get user statistics
  async getUserStats(): Promise<UserStats> {
    const response = await api.get<UserStats>(`${USERS_ENDPOINT}/me/stats`);
    return response.data;
  },

  async exportCurrentUserData(): Promise<Blob> {
    const response = await api.get<Blob>(`${USERS_ENDPOINT}/me/export`, {
      responseType: "blob",
    });
    return response.data;
  },

  async exportPrivateQuotes(format: "csv" | "markdown"): Promise<Blob> {
    const response = await api.get<Blob>("/journal-entries/export/citations", {
      params: { format },
      responseType: "blob",
    });
    return response.data;
  },

  async deleteCurrentUser(password: string, confirmation: string): Promise<void> {
    await api.delete(`${USERS_ENDPOINT}/me`, {
      data: { password, confirmation },
    });
  },
};
